import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RedirectCommand,
  Router,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import * as fromGenerated from '../../_generated';
import { RouterService } from '../shared/services/router.service';
import {
  hasMessages,
  hasOwnSkill,
  isDraft,
} from '../store/chat-lifecycle.helper';
import { ChatActions, ChatWithMessagesActions } from '../store/chats.actions';
import { chatsFeature } from '../store/chats.reducer';
import { parseChatUuid, parseSkillHint } from './conversation-address.helper';

/**
 * Decides the address of the conversation route before anything renders
 * (SB-3944 / KD-29), so a cold load never shows a wrong chat and never needs a
 * second navigation. Every redirect replaces the history entry (KD-48).
 *
 * - a malformed `?assistantUuid` goes to /not-found;
 * - no id, a numeric id (an old address, KD-32) or a non-uuid id opens a new
 *   chat;
 * - a uuid the store holds activates; a created chat it holds with a skill
 *   hint drops the hint (KD-59), a draft keeps it;
 * - otherwise the server answers (KD-27): the read of the chat with its
 *   messages (`GET /chats/:uuid/messages`, the one the page loads from) gives
 *   200 to open the chat, 404 to open a new chat, and any other error shows the
 *   default error toast and opens a new chat (never /not-found). The chat that
 *   read brought in is not read again by the selection that follows;
 * - a 200 for a chat with no user message opens a new chat too, with no toast
 *   (KD-53): that chat belongs to the tab that started it, which the store
 *   check above already let through.
 *
 * An affordance, not the control (constitution Article II): whether a uuid
 * opens is the server's caller-scoped read of the chat. The guard maps a 200
 * with a user message to "activate", and a 404 or a 200 without one to "new
 * chat".
 *
 * A new chat keeps the `?assistantUuid` hint (KD-26), so a reload of an unsent
 * skill chat keeps its skill. Without a hint, a chat with no user message
 * gives a new chat of its own skill (KD-59: the hint left when the chat was
 * created, at its first file too). A chat that opens drops the hint from the
 * address, after the chat is loaded (KD-59).
 */
export const conversationCanActivateGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
) => {
  // Every `inject` runs before the first `await`: an injection context does
  // not survive one.
  const router = inject(Router);
  const routerService = inject(RouterService);
  const store = inject(Store);
  const chatsService = inject(fromGenerated.ChatsService);
  const toastr = inject(ToastrService);
  const translate = inject(TranslateService);

  const hint = parseSkillHint(route.queryParams['assistantUuid']);
  if (hint.kind === 'malformed') {
    return new RedirectCommand(router.parseUrl('/not-found'), {
      replaceUrl: true,
    });
  }
  const skill = hint.kind === 'skill' ? hint.uuid : undefined;
  const fresh = (draftSkill = skill) =>
    new RedirectCommand(routerService.newChatUrl(draftSkill), {
      replaceUrl: true,
    });

  const uuid = parseChatUuid(route.params['id']);
  if (!uuid) {
    return fresh();
  }
  // SB-3944 / KD-59: the address of a created chat without its hint, every
  // other query param kept.
  const bare = () =>
    new RedirectCommand(
      router.createUrlTree(['/chats/conversation', uuid], {
        queryParams: Object.fromEntries(
          Object.entries(route.queryParams).filter(
            ([name]) => name !== 'assistantUuid',
          ),
        ),
      }),
      { replaceUrl: true },
    );

  const held = store.selectSignal(chatsFeature.selectChatByUuid(uuid))();
  if (held) {
    // A created chat never keeps the hint, even on Back to an entry the
    // replace after its first send could not reach. A draft keeps it.
    return skill && !isDraft(held) ? bare() : true;
  }

  try {
    const chatWithMessages = await firstValueFrom(
      chatsService.chatsControllerGetChatWithMessagesV1(uuid),
    );
    // SB-3944 / KD-53: a chat with no user message belongs to the tab that
    // started it. Another tab gets its own draft, silently, and never loads
    // this row.
    if (!hasMessages(chatWithMessages)) {
      // SB-3944 / KD-59: a chat created by its first file has no message and
      // no hint left in its address; its own skill comes from the server. A
      // hint still wins.
      return fresh(
        skill ??
          (hasOwnSkill(chatWithMessages)
            ? (chatWithMessages.assistantId ?? undefined)
            : undefined),
      );
    }
    // Not `loadChatsSuccess`: that sets `loaded`, so the chats resolver would
    // skip the list and the sidebar would hold this one chat.
    store.dispatch(
      ChatWithMessagesActions.loadChatWithMessagesSuccess({
        chatWithMessages,
      }),
    );
    // The selection that follows the navigation reuses this read.
    store.dispatch(ChatActions.chatPreloaded({ uuid }));
    // SB-3944 / KD-59: a pasted or bookmarked hint leaves the address. The
    // chat is in the store now, so the redirect's own guard pass reads nothing.
    return skill ? bare() : true;
  } catch (error) {
    if (!(error instanceof HttpErrorResponse && error.status === 404)) {
      // `get`, not `instant`: on a cold load the guard runs before any page,
      // and `get` waits for the translation file.
      toastr.error(
        (await firstValueFrom(translate.get('TOAST.ERROR.DEFAULT'))) as string,
      );
    }
    return fresh();
  }
};
